// Import js-beautify for formatting
import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import Cookies from 'js-cookie';

/**
 * Merges class strings using Tailwind CSS class merging
 * @param {...string} inputs - Class strings to merge
 * @returns {string} - The merged class string
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Generates a standardised automation class for a component or element.
 *
 * Part of the cross-library `at-*` CSS class convention used by automated
 * test tooling (e.g. Perfecto). Classes are always present when the component
 * renders — they are unconditional and do not depend on consumer props.
 *
 * Naming rules:
 *   - Component root:         `at-{componentname}`            e.g. `at-productcard`
 *   - Entity-specific root:   `at-{componentname}-{id}`       e.g. `at-productcard-123456`
 *   - Interactive element:    `at-{action}`                   e.g. `at-qty-minus`
 *   - Data display element:   `at-{dataname}`                 e.g. `at-shelflife`
 *
 * Component names are flattened to lowercase with no word separators.
 * Entity IDs are appended as-is (they come from the backend and are stable).
 *
 * @param {string} name - The base automation name (e.g. `'productcard'`, `'qty-minus'`)
 * @param {string} [id] - Optional entity ID to append (e.g. a product code)
 * @returns {string} The `at-*` class string, e.g. `'at-productcard'` or `'at-productcard-123456'`
 *
 * @example
 * atClass('productcard')           // → 'at-productcard'
 * atClass('productcard', '12345')  // → 'at-productcard-12345'
 * atClass('qty-minus')             // → 'at-qty-minus'
 */
export function atClass(name, id) {
  return id != null && id !== '' ? `at-${name}-${id}` : `at-${name}`;
}

/**
 * Hook for accessing the current theme
 * @returns {string} - Current theme ('booker' or 'venus')
 */
export { useTheme } from '../hooks/useTheme';

/**
 * Get the current theme ID from the DOM
 * @returns {string} - Current theme ID ('booker' or 'venus')
 */
export const getThemeId = () => {
  if (typeof document === 'undefined') return 'booker';
  return document.documentElement?.getAttribute('data-theme') || 'booker';
};

/**
 * Get brand color for the current theme
 *
 * @param {string} [themeId] - Optional theme ID. If not provided, will get from DOM
 * @param {string} [colorKey='primary.DEFAULT'] - The color key to retrieve (e.g., 'primary.500', 'secondary.1000')
 * @returns {string} - The brand color hex value
 */
export const getBrandColor = (themeId, colorKey = 'primary.DEFAULT') => {
  const themeColors = {
    booker: {
      primary: {
        DEFAULT: '#FC4C02',
        50: '#FFF0EB',
        100: '#FFE6DF',
        200: '#FFD0C2',
        300: '#FFA185',
        400: '#FF7349',
        500: '#FC4C02',
        600: '#DD3300',
        700: '#AA2700',
        800: '#401103',
      },
      secondary: {
        DEFAULT: '#FAFAF5',
        1000: '#FAFAF5',
        1100: '#F5F4F0',
        1200: '#EBEAE6',
        1300: '#D1CFC7',
        2000: '#1F1E19',
      },
    },
    venus: {
      primary: {
        DEFAULT: '#901438',
        50: '#FDF2F8',
        100: '#FCE7F3',
        200: '#F8C7E7',
        300: '#F5A7DB',
        400: '#E878C1',
        500: '#901438',
        600: '#C41585',
        700: '#A00E66',
        800: '#7D0A52',
      },
      secondary: {
        DEFAULT: '#FAF9F7',
        1000: '#FAF9F7',
        1100: '#F5F4F0',
        1200: '#EBEAE6',
        1300: '#D1CFC7',
        2000: '#423F38',
      },
    },
  };

  // If themeId is not provided or is actually a color key, handle it
  const actualThemeId =
    themeId && (themeId === 'booker' || themeId === 'venus')
      ? themeId
      : getThemeId();

  const actualColorKey =
    themeId && themeId !== 'booker' && themeId !== 'venus' ? themeId : colorKey;

  const theme = themeColors[actualThemeId] || themeColors.booker;

  // Handle nested color keys like 'primary.500'
  const keys = actualColorKey.split('.');
  let color = theme;

  for (const key of keys) {
    color = color?.[key];
    if (!color) break;
  }

  return color || themeColors.booker.primary.DEFAULT;
};

/**
 * Hook for checking media query matches
 * @param {string} query - Media query string to match
 * @returns {boolean} - Whether the media query matches
 */

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      setMatches(false);
      return undefined;
    }

    const matchMedia = window.matchMedia(query);

    const updateMatch = () => {
      setMatches(matchMedia.matches);
    };

    updateMatch();

    if (typeof matchMedia.addEventListener === 'function') {
      matchMedia.addEventListener('change', updateMatch);
    } else if (typeof matchMedia.addListener === 'function') {
      matchMedia.addListener(updateMatch);
    }

    return () => {
      if (typeof matchMedia.removeEventListener === 'function') {
        matchMedia.removeEventListener('change', updateMatch);
      } else if (typeof matchMedia.removeListener === 'function') {
        matchMedia.removeListener(updateMatch);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Parses an HTML string into renderable content.
 *
 * In a browser environment, this returns an array of parsed nodes
 * (strings and JSX elements). When `document` is not available
 * (e.g. during SSR or in a non-browser environment), it returns
 * the original `htmlString`. If `htmlString` is falsy, it returns
 * an empty string.
 *
 * @param {string} htmlString - The HTML string to parse
 * @returns {(Array|string)} Parsed content, as described above.
 */
export const parseHtmlToElements = (htmlString) => {
  if (!htmlString) return '';
  if (typeof document === 'undefined') return htmlString;

  const div = document.createElement('div');
  div.innerHTML = htmlString;

  return Array.from(div.childNodes).map((node, index) => {
    if (node.nodeType === 3) {
      // Text node
      return node.textContent;
    }
    const Element = node.tagName.toLowerCase();
    const attributes = Array.from(node.attributes).reduce(
      (acc, attr) => {
        // Convert 'class' to 'className' but keep the classes as-is
        if (attr.name === 'class') {
          return {
            ...acc,
            className: attr.value,
          };
        }
        return {
          ...acc,
          [attr.name]: attr.value,
        };
      },
      { key: index },
    );

    return (
      <Element key={index} {...attributes}>
        {parseHtmlToElements(node.innerHTML)}
      </Element>
    );
  });
};

/**
 * Formats a timestamp into a human-readable string like "tomorrow at 9pm"
 * @param {number|string|Date} timestamp - The timestamp to format (can be a Date object, timestamp number, or ISO string)
 * @returns {string} - Formatted date string
 */
export const formatRelativeTime = (timestamp) => {
  // Convert input to Date object
  const date = new Date(timestamp);

  // Get current date for comparison
  const now = new Date();

  // Set both dates to midnight for day comparison
  const todayMidnight = new Date(now);
  todayMidnight.setHours(0, 0, 0, 0);

  const dateMidnight = new Date(date);
  dateMidnight.setHours(0, 0, 0, 0);

  // Calculate difference in days
  const diffDays = Math.round(
    (dateMidnight - todayMidnight) / (1000 * 60 * 60 * 24),
  );

  // Format the time
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Format time string
  const timeString =
    minutes === 0
      ? `${hours}${ampm}`
      : `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;

  // Determine day description
  let dayString;
  if (diffDays === 0) {
    dayString = 'today';
  } else if (diffDays === 1) {
    dayString = 'tomorrow';
  } else if (diffDays === -1) {
    dayString = 'yesterday';
  } else if (diffDays > 1 && diffDays < 7) {
    // Get day name for dates within a week
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    dayString = days[date.getDay()];
  } else {
    // Format as date for dates further away
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    dayString = `${day} ${month}`;
  }

  return `${dayString} at ${timeString}`;
};

/**
 * Formats a number into a currency string
 * @param {number} amount - The number to format
 * @param {string} currency - The currency code (e.g. 'USD', 'EUR')
 * @returns {string} - The formatted currency string
 */
export function formatCurrency(amount, currency = 'GBP', options) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    ...options,
  }).format(amount);
}

/**
 * Creates a debounced version of a function
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} - The debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Creates a throttled version of a function
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time limit between function calls in milliseconds
 * @returns {Function} - The throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Fetches a file from a URL and triggers a browser download.
 * Falls back to navigation if the fetch fails (e.g. CORS restrictions).
 *
 * @param {string} url - The URL of the file to download
 * @param {string} filename - The suggested filename for the download
 * @returns {Promise<void>}
 */
export const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.location.href = url;
  }
};

/**
 * Gets a cookie in the browser
 * @param {string} name - The name of the cookie
 */
export const getCookie = (name) => {
  return Cookies.get(name);
}

/**
 * Checks if the user is currently logged in.
 * The user is considered logged in when the "context" cookie is present.
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return Boolean(Cookies.get('context'));
};

/**
 * Checks if the user is currently logged out.
 * The user is considered logged out when the "context" cookie is absent.
 * @returns {boolean}
 */
export const isLoggedOut = () => {
  return !Cookies.get('context');
};

export const displayErrors= (value) => {
  if (typeof value === 'string') {
    return <>{value}</>;
  }

  if (Array.isArray(value)) {
    return (
      <ul>
        {value.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  }

  return <p>Unsupported type</p>;
}

export function smoothScrollTo(yTarget, duration = 600) {
  const startY = window.scrollY || window.pageYOffset;
  const distance = yTarget - startY;
  const startTime = performance.now();

  function easeInOutQuad(t) {
    return t < 0.5
      ? 2 * t * t
      : -1 + (4 - 2 * t) * t;
  }

  function scroll(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease = easeInOutQuad(progress);
    window.scrollTo(0, startY + distance * ease);

    if (elapsed < duration) {
      requestAnimationFrame(scroll);
    }
  }

  requestAnimationFrame(scroll);
}

export function scrollToElement(selector, duration = 600) {
  const element = document.querySelector(selector);
  if (element) {
    const y = element.offsetTop;
    smoothScrollTo(y, duration);
  }
}

export function getCopyrightWording() {
  switch(  document?.documentElement?.getAttribute('data-theme') || 'booker') {
    case 'venus':
      return '© Venus Ltd ' + new Date().getFullYear() + '. All rights reserved.'
    default:
      return '© Booker Group ' + new Date().getFullYear() + '. All rights reserved.'
  }
}

/**
 * @param {Object} ad - Adverts pipeline entry
 * @param {number} columns - Current number of grid columns
 * @param {number} [offset=0] - Grid items rendered before the product cards (e.g. featured products)
 * @returns {number} Index of the product the entry is rendered before
 */
export function getAdvertPlacement(ad, columns, offset = 0) {
  return ad.row ? Math.max(0, ad.row * columns - offset) : ad.placement;
}

/**
 * Grid cell classes of an entry from the booker2-adverts pipeline.
 *
 * @param {Object} ad - Adverts pipeline entry
 * @returns {string} Full width for banners, advert cell otherwise
 */
export function getAdvertClassname(ad) {
  return ad.fullwidth ? 'tw:col-span-full' : 'tw:col-span-1 tw:sm:col-span-2 tw:lg:col-span-1';
}

/**
 * @param {Object} ad - Adverts pipeline entry
 * @returns {Object|undefined} Inline style for the entry wrapper
 */
export function getAdvertStyle(ad) {
  return ad.row ? { gridRowStart: ad.row + 1 } : undefined;
}